import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { Database } from '../../lib/database.types';
import './Registration.css';
type SupervisorProfileInsert = Database['public']['Tables']['supervisor_profiles']['Insert'] & { id: string };
type StudentProfileInsert = Database['public']['Tables']['student_profile']['Insert'] & { id: string };

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Check if there's a verification code in the URL
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        
        let session = null;
        
        if (code) {
          setVerificationStatus('Processing email verification...');
          
          // Exchange the code for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            // If exchange fails, try to get existing session as fallback
            console.warn('Code exchange failed, trying to get existing session:', error);
            const { data: { session: fallbackSession }, error: sessionError } = await supabase.auth.getSession();
            
            if (sessionError || !fallbackSession) {
              // Both methods failed, but continue anyway - profile might already exist
              console.warn('Could not establish session, but continuing verification');
            } else {
              session = fallbackSession;
            }
          } else if (data.session) {
            session = data.session;
            setVerificationStatus('Email verified! Setting up your profile...');
            
            // Clean up the URL by removing the code parameter
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('code');
            window.history.replaceState({}, '', newUrl.toString());
          }
        }

        // Check for student registration data
        let pendingRegistration = localStorage.getItem('pendingRegistration');
        let isSupervisor = false;
        
        // If no student registration, check for supervisor registration
        if (!pendingRegistration) {
          pendingRegistration = localStorage.getItem('pendingSupervisorRegistration');
          isSupervisor = true;
          
          if (!pendingRegistration) {
            throw new Error('Registration data not found');
          }
        }

        const registrationData = JSON.parse(pendingRegistration);

        // Get the current session if we don't already have one from code exchange
        if (!session) {
          const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            // If we can't get session but have registration data, profile might already exist
            // Continue with verification attempt - we'll check for existing profile
            console.warn('Could not get session, but continuing verification:', sessionError);
          } else {
            session = currentSession;
          }
        }
        
        // Get user ID from session or auth state
        let userId: string | null = null;
        if (session?.user) {
          userId = session.user.id;
        } else {
          // Try to get user ID from auth state even without session
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            userId = user.id;
          }
        }

        if (!userId) {
          throw new Error('No authenticated user found. Please try logging in again.');
        }
        
        // Check if email is already verified
        if (session?.user) {
          setVerificationStatus(
            session.user.email_confirmed_at 
              ? 'Your email is already verified. Setting up your profile...' 
              : 'Verifying your email...'
          );
        } else {
          setVerificationStatus('Setting up your profile...');
        }

        let profileError;
        let profileExists = false;
        
        if (isSupervisor) {
          // Check if profile already exists
          const { data: existingProfiles, error: checkError } = await supabase
            .from('supervisor_profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle();

          if (checkError && checkError.code !== 'PGRST116') {
            // Error other than "not found" - log but continue
            console.warn('Error checking for existing supervisor profile:', checkError);
          }

          if (existingProfiles) {
            // Profile already exists
            profileExists = true;
            setVerificationStatus('Profile already exists. Redirecting to login...');
          } else {
            // Create supervisor profile if it doesn't exist
            const supervisorProfileData: SupervisorProfileInsert = {
              id: userId,
              first_name: registrationData.first_name,
              last_name: registrationData.last_name,
              office: registrationData.office,
              role_title: registrationData.role_title,
              email: registrationData.email,
              user_role: registrationData.user_role
            };
            
            const { error } = await supabase
              .from('supervisor_profiles')
              .insert([supervisorProfileData] as any);
              
            // Handle duplicate key errors (profile was created between check and insert)
            if (error) {
              if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
                // Duplicate key error - profile already exists, treat as success
                profileExists = true;
                profileError = null;
              } else {
                profileError = error;
              }
            }
          }
          
          // Clear supervisor registration data in any case
          localStorage.removeItem('pendingSupervisorRegistration');
        } else {
          // Check if student profile already exists
          const { data: existingProfile, error: checkError } = await supabase
            .from('student_profile')
            .select('*')
            .eq('id', userId)
            .maybeSingle();

          if (checkError && checkError.code !== 'PGRST116') {
            // Error other than "not found" - log but continue
            console.warn('Error checking for existing student profile:', checkError);
          }

          if (existingProfile) {
            // Profile already exists
            profileExists = true;
            setVerificationStatus('Profile already exists. Redirecting to login...');
          } else {
            // Create student profile
            const studentProfileData: StudentProfileInsert = {
              id: userId,
              first_name: registrationData.first_name,
              last_name: registrationData.last_name,
              student_id: registrationData.student_id,
              course_info: registrationData.course_info,
              department: registrationData.department,
              supervisor: registrationData.supervisor,
              email: registrationData.email,
              role: registrationData.role
            };
            
            const { error } = await supabase
              .from('student_profile')
              .insert([studentProfileData] as any);
              
            // Handle duplicate key errors (profile was created between check and insert)
            if (error) {
              if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique')) {
                // Duplicate key error - profile already exists, treat as success
                profileExists = true;
                profileError = null;
              } else {
                profileError = error;
              }
            }
          }
          
          // Clear student registration data
          localStorage.removeItem('pendingRegistration');
        }

        // Only throw error if it's not a duplicate key error
        if (profileError) {
          throw profileError;
        }

        // Sign out the user so they need to log in manually
        // Don't throw if sign out fails - we still want to redirect
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.warn('Sign out failed, but continuing with redirect:', signOutError);
        }

        // Set appropriate success message and redirect
        const successMessage = profileExists 
          ? 'Email verified successfully! Your profile is ready. Redirecting to login...'
          : 'Email verified successfully! Your profile has been created. Redirecting to login...';
        
        setVerificationStatus(successMessage);
        
        // Add a slight delay before redirecting to show the success message
        setTimeout(() => {
          navigate('/', { 
            state: { 
              message: profileExists
                ? 'Email verified successfully! Your profile is ready. You can now log in.'
                : 'Email verified successfully! Your profile has been created. You can now log in.'
            } 
          });
        }, 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred during verification');
      } finally {
        setVerifying(false);
      }
    };

    handleEmailVerification();
  }, [navigate, location]);

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="logo">
            <h1>Intern Trackr</h1>
            <p className="tagline">Anytime. Anywhere.</p>
          </div>
          <div className="verifying-message">
            <p>{verificationStatus || "Verifying your email..."}</p>
            {verificationStatus.includes('Redirecting') && (
              <div className="verification-progress">
                <div className="loader"></div>
                <p className="redirect-message">Redirecting you to login...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="logo">
            <h1>Intern Trackr</h1>
            <p className="tagline">Anytime. Anywhere.</p>
          </div>
          <div className="error-message">
            <p>Error verifying email: {error}</p>
            <button 
              className="login-button"
              onClick={() => navigate('/')}
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default VerifyEmail;
